import { useQuery } from "@tanstack/react-query";
import { Link, getRouteApi, useNavigate } from "@tanstack/react-router";
import { searchCertificates, type Certificate } from "@/api/client";
import { DataTable, type Column } from "@/components/DataTable";
import { PagedFooter } from "@/components/Pagination";
import { useBatchLabel } from "@/components/batchLabel";
import { formatDate } from "@/components/format";
import { EmptyState, ErrorBanner, StatusBadge, WorkspaceHeader } from "@/components/ui";
import { pageParams } from "@/routes/paging";

const certificatesApi = getRouteApi("/staff/certificates");

const columns: Column<Certificate>[] = [
  {
    key: "document",
    header: "Document",
    primary: true,
    cell: (cert) => (
      <Link
        to="/certificates/$certificateId"
        params={{ certificateId: cert.id }}
        className="inline-flex min-h-10 items-center font-mono text-xs font-medium text-zinc-950 underline-offset-2 hover:underline md:inline md:min-h-0"
      >
        {cert.doc_control_id}
      </Link>
    ),
  },
  {
    key: "member",
    header: "Member",
    cell: (cert) => (
      <>
        <span className="block font-medium text-zinc-950">{cert.member_full_name}</span>
        <span className="block font-mono text-xs text-zinc-500">{cert.membership_no ?? "-"}</span>
      </>
    ),
  },
  { key: "program", header: "Program", cell: (cert) => <span className="text-zinc-700">{cert.program_name ?? "-"}</span> },
  { key: "status", header: "Status", cell: (cert) => <StatusBadge status={cert.status} /> },
  { key: "issued", header: "Issued", cell: (cert) => <span className="text-zinc-500">{formatDate(cert.issued_at)}</span> },
];

export function CertificateSearch() {
  const search = certificatesApi.useSearch();
  const { q, batch, page, page_size } = search;
  const batchName = useBatchLabel(batch);
  const navigate = useNavigate();

  const results = useQuery({
    queryKey: ["certificates", q ?? "", batch ?? "", page, page_size],
    queryFn: () => searchCertificates({ q, batchId: batch, ...pageParams(search) }),
  });
  const items = results.data?.items ?? [];

  function submit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const text = String(new FormData(e.currentTarget).get("q") ?? "").trim();
    void navigate({ to: "/certificates", search: { q: text || undefined, batch, page_size } });
  }

  return (
    <section className="space-y-5">
      <WorkspaceHeader title="Certificates" detail="Search by name, membership number, document-control ID, or program" />

      <form role="search" onSubmit={submit} className="flex flex-col gap-2 md:flex-row md:flex-wrap md:items-center">
        {/* key resets the uncontrolled field when back/forward changes ?q= */}
        <input
          key={q ?? ""}
          name="q"
          aria-label="Search certificates"
          defaultValue={q ?? ""}
          placeholder="e.g. Ada Okafor, A00012, CIPM/ASSOCIATE/2026"
          className="flex-1 rounded-md border border-zinc-300 bg-white px-3 py-2 text-sm md:min-w-72 outline-none focus:border-brand focus:ring-1 focus:ring-brand"
        />
        <button type="submit" className="min-h-10 rounded-md bg-brand-strong px-3 py-2 text-sm font-medium text-white hover:bg-brand-stronger md:min-h-0">
          Search
        </button>
        {batch ? (
          <Link
            to="/certificates"
            search={{ q, page_size }}
            aria-label={`Remove filter Batch: ${batchName}`}
            className="rounded-full bg-zinc-100 px-3 py-1 text-xs font-medium text-zinc-700 hover:bg-zinc-200"
          >
            <span>Batch: {batchName}</span>
            <span aria-hidden="true"> ×</span>
          </Link>
        ) : null}
      </form>

      <ErrorBanner error={results.error} />

      <div className="overflow-hidden rounded-lg border border-zinc-200 bg-white">
        {results.isLoading ? (
          <EmptyState title="Searching" detail="Looking up certificates." />
        ) : items.length === 0 ? (
          <EmptyState title="No certificates found" detail="Try a different name, number, or program." />
        ) : (
          <div className="p-3 md:p-0">
            <DataTable rows={items} columns={columns} rowKey={(cert) => cert.id} label="Certificates" tableClassName="min-w-[760px]" />
          </div>
        )}
        <PagedFooter data={results.data} page={page} pageSize={page_size} />
      </div>
    </section>
  );
}
